import { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Button, Flex, FullscreenTakeover, LoadingIndicator, Text, toast } from "@jsq/ds/core";
import { useGetArenaQuery } from "~/graphql/mainHooks";
import { CustomReportRecipientObjectType } from "~/graphql/supergraphEnums";
import {
  useArchiveReportTemplateMutation,
  useGetDataPackageLazyQuery,
  useGetReportTemplateQuery,
  useGetReportTemplatesByArenaQuery,
  useRestoreDataPackageMutation,
  useRestoreReportTemplateMutation,
  useUpdateReportTemplateMutation,
} from "~/graphql/supergraphHooks";
import { getErrorMessage } from "~/utils/errors";
import { toDbId, toGlobalId } from "~/utils/graphql-helpers";
import { ReportingMissingPermissionState } from "../components/MissingPermissionState";
import { missingDatesPrompt, usePreviewDateParameters } from "../dateParameters";
import { PreviewPanel } from "../ReportTemplateEditor/components/PreviewPanel";
import { usePreviewPositions } from "../ReportTemplateEditor/hooks/usePreviewPositions";
import {
  getLatestVersion,
  isTransactionBasedReport,
  type PreviewEntityInfo,
} from "../ReportTemplateEditor/types";
import { isUnauthorizedError } from "../utils";
import { ConfirmArchiveTemplateModal } from "./components/ConfirmArchiveTemplateModal";
import { ConfirmRestoreTemplateModal } from "./components/ConfirmRestoreTemplateModal";
import { TemplateDetailsSidebar } from "./components/TemplateDetailsSidebar";
import { UpdateTemplateNameModal } from "./components/UpdateTemplateNameModal";
import { useTemplatePreviewJob } from "./hooks/useTemplatePreviewJob";
import * as styles from "./styles_vanilla.css";

type ViewerParams = {
  arenaId?: string;
  templateId: string;
};

type ViewerLocationState = {
  sourceArenaId?: number;
};

export const ReportTemplateViewer = () => {
  const { arenaId, templateId } = useParams<ViewerParams>();
  const history = useHistory();
  const location = useLocation<ViewerLocationState>();
  const templateIdNum = Number(templateId);
  const sourceArenaId = location.state?.sourceArenaId ?? null;

  const {
    data,
    loading,
    error: templateError,
    refetch,
  } = useGetReportTemplateQuery({
    variables: { id: templateIdNum },
    skip: Number.isNaN(templateIdNum),
    fetchPolicy: "cache-and-network",
  });

  const template = data?.reportTemplate;
  const isSystemTemplate = !arenaId || arenaId === "system";
  const arenaIdNum = isSystemTemplate ? null : Number(arenaId);

  const { data: arenaData } = useGetArenaQuery({
    variables: { id: toGlobalId("ArenaObjectType", arenaIdNum ?? 0) },
    skip: isSystemTemplate || arenaIdNum == null || Number.isNaN(arenaIdNum),
  });

  const arenaName = isSystemTemplate ? "System template" : (arenaData?.arena?.name ?? "");

  const { data: templatesData } = useGetReportTemplatesByArenaQuery({
    variables: { arenaId: isSystemTemplate ? null : arenaIdNum },
    skip: Number.isNaN(arenaIdNum ?? 0),
  });

  const existingTemplateNames = useMemo(
    () => new Set((templatesData?.reportTemplatesByArena ?? []).map((t) => t.name)),
    [templatesData],
  );

  const effectiveVersion = useMemo(
    () => getLatestVersion(template?.currentVersion, template?.versions),
    [template?.currentVersion, template?.versions],
  );

  const hasTemplateFile = Boolean(effectiveVersion?.downloadUrl);

  const transactionBased = isTransactionBasedReport(template?.reportType ?? "");

  const [selectedEntity, setSelectedEntity] = useState<PreviewEntityInfo | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  // Transaction-based reports (Capital Call, Distribution, etc.) select multiple
  // entities and transactions instead of a single entity + account.
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<string[]>([]);
  const [previewArenaGlobalId, setPreviewArenaGlobalId] = useState("");

  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  const [archiveTemplate, { loading: isArchiving }] = useArchiveReportTemplateMutation();
  const [restoreTemplate, { loading: isRestoringTemplate }] =
    useRestoreReportTemplateMutation();
  const [restoreDataPackage, { loading: isRestoringDataPackage }] =
    useRestoreDataPackageMutation();
  const [fetchDataPackage] = useGetDataPackageLazyQuery({ fetchPolicy: "no-cache" });
  const [updateTemplate, { loading: isUpdating }] = useUpdateReportTemplateMutation();

  const isRestoring = isRestoringTemplate || isRestoringDataPackage;

  // The recipient account list is resolved from the selected entity:
  // account-statement uses the single selected entity; transaction-based uses
  // the first selected entity.
  const entityIdForAccounts = transactionBased
    ? (selectedEntityIds[0] ?? "")
    : (selectedEntity?.id ?? "");
  const { accounts, loading: accountsLoading } = usePreviewPositions(entityIdForAccounts);

  // Both paths: the user explicitly picks the recipient account.
  const previewAccountId = selectedAccountId;
  const recipientAccountDbId = previewAccountId ? toDbId(previewAccountId) : null;

  // Keep the GL transaction IDs as strings end-to-end so IDs larger than
  // Number.MAX_SAFE_INTEGER survive the round-trip to the preview job.
  const glTransactionIds = transactionBased ? selectedTransactionIds.filter(Boolean) : null;

  const hasEntities = transactionBased
    ? selectedEntityIds.length > 0
    : Boolean(selectedEntity?.id);
  const hasTransactions = selectedTransactionIds.length > 0;

  // The query is scoped to the arena that owns the data package (null for
  // system packages), which is not necessarily the arena being previewed against.
  const dateParameters = usePreviewDateParameters({
    queryId: effectiveVersion?.queryId ?? null,
    arenaId: template?.dataPackage?.arenaId ?? null,
  });

  const { pdfUrl, isRendering, renderError } = useTemplatePreviewJob({
    reportTemplateId: templateIdNum,
    reportTemplateVersionId: effectiveVersion?.id ?? null,
    recipientObjectType: CustomReportRecipientObjectType.Account,
    recipientObjectId: recipientAccountDbId,
    arenaGlobalId: previewArenaGlobalId,
    parameters: dateParameters.parameters,
    glTransactionIds,
    skip:
      (transactionBased
        ? !hasEntities || !hasTransactions || !previewAccountId
        : !selectedEntity?.id) ||
      !hasTemplateFile ||
      !dateParameters.isResolved ||
      !dateParameters.isComplete,
  });

  const systemFallbackRoute = sourceArenaId ? `/reporting/${sourceArenaId}` : "/reporting";

  const navigateBack = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.push(isSystemTemplate ? systemFallbackRoute : `/reporting/${arenaId}`);
  }, [history, isSystemTemplate, arenaId, systemFallbackRoute]);

  const handleArchive = useCallback(async () => {
    try {
      await archiveTemplate({ variables: { id: templateIdNum } });
      toast.success("Template has been archived");
      setIsArchiveModalOpen(false);
      navigateBack();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, [archiveTemplate, templateIdNum, navigateBack]);

  const handleRestore = useCallback(async () => {
    const linkedDataPackage = template?.dataPackage;

    try {
      if (linkedDataPackage) {
        const { data: dataPackageData } = await fetchDataPackage({
          variables: { arenaId: arenaIdNum, dataPackageId: linkedDataPackage.id },
        });
        const fetched = dataPackageData?.finpubDataPackage;
        if (fetched && fetched.isActive === false) {
          const { data: restoreResult } = await restoreDataPackage({
            variables: {
              arenaId: fetched.arenaId ?? null,
              dataPackageId: fetched.id,
            },
          });
          if (!restoreResult?.finpubDataPackageRestore?.success) {
            toast.error(
              restoreResult?.finpubDataPackageRestore?.message ??
                "Failed to restore data package",
            );
            return;
          }
        }
      }

      await restoreTemplate({ variables: { id: templateIdNum } });
      toast.success("Template has been restored");
      history.push(isSystemTemplate ? systemFallbackRoute : `/reporting/${arenaId}`);
      setIsRestoreModalOpen(false);
      void refetch();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, [
    template?.dataPackage,
    fetchDataPackage,
    arenaIdNum,
    restoreDataPackage,
    restoreTemplate,
    templateIdNum,
    history,
    isSystemTemplate,
    systemFallbackRoute,
    arenaId,
    refetch,
  ]);

  const handleRename = useCallback(
    async (newName: string) => {
      try {
        await updateTemplate({
          variables: { id: templateIdNum, input: { name: newName } },
        });
        toast.success("Template name updated");
        setIsRenameModalOpen(false);
        void refetch();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    },
    [updateTemplate, templateIdNum, refetch],
  );

  if (templateError && isUnauthorizedError(templateError)) {
    return <ReportingMissingPermissionState />;
  }

  if (loading && !template) {
    return (
      <FullscreenTakeover disablePortal>
        <FullscreenTakeover.Header
          title="Loading..."
          leftActionContent={
            <Button variant="muted" icon="xmark" onClick={navigateBack}>
              Exit
            </Button>
          }
        />
        <FullscreenTakeover.Body>
          <Flex alignItems="center" justifyContent="center" className={styles.centeredBody}>
            <LoadingIndicator />
          </Flex>
        </FullscreenTakeover.Body>
      </FullscreenTakeover>
    );
  }

  if (!template) {
    return (
      <FullscreenTakeover disablePortal>
        <FullscreenTakeover.Header
          title="Template not found"
          leftActionContent={
            <Button variant="muted" icon="xmark" onClick={navigateBack}>
              Exit
            </Button>
          }
        />
        <FullscreenTakeover.Body>
          <Flex alignItems="center" justifyContent="center" className={styles.centeredBody}>
            <Text variant="regular-md">Template not found</Text>
          </Flex>
        </FullscreenTakeover.Body>
      </FullscreenTakeover>
    );
  }

  const sidebarTemplate = {
    id: template.id,
    name: template.name,
    reportType: template.reportType,
    arenaId: template.arenaId ?? null,
    dataPackageName: template.dataPackage?.name ?? null,
    updatedAt: template.updatedAt,
    updatedByUser: template.updatedByUser ?? null,
    archivedAt: template.archivedAt ?? null,
    archivedByUser: template.archivedByUser ?? null,
    currentVersion: effectiveVersion
      ? {
          version: effectiveVersion.version,
          status: effectiveVersion.status,
          downloadUrl: effectiveVersion.downloadUrl ?? null,
          review: effectiveVersion.review ?? null,
        }
      : null,
  };

  const placeholder = (() => {
    if (!hasTemplateFile) {
      return (
        <>
          <Text variant="h3" marginBottom="md">
            No template file available
          </Text>
          <Text variant="secondary-md">
            This template does not have an uploaded file to preview.
          </Text>
        </>
      );
    }
    if (!hasEntities) {
      return (
        <>
          <Text variant="h3" marginBottom="md">
            Select an entity to preview
          </Text>
          <Text variant="secondary-md">
            Choose an entity to generate a preview with sample data.
          </Text>
        </>
      );
    }
    if (transactionBased && !hasTransactions) {
      // No transactions selected yet: the preview job hasn't been kicked off
      // (the hook skips until there's a selection), so don't imply work is in progress.
      return (
        <>
          <Text variant="h3" marginBottom="md">
            Select transactions to preview
          </Text>
          <Text variant="secondary-md">
            Choose one or more transactions to generate a preview.
          </Text>
        </>
      );
    }
    if (!selectedAccountId) {
      // No recipient account selected yet: the preview job hasn't been kicked off
      // (the hook skips until there's a recipient), so don't imply work is in progress.
      return (
        <>
          <Text variant="h3" marginBottom="md">
            Select an account to preview
          </Text>
          <Text variant="secondary-md">
            Choose an account to generate a preview with sample data.
          </Text>
        </>
      );
    }
    // This query resolves its dates from parameters supplied at run time, so
    // there is nothing to render until each one has a value.
    if (dateParameters.isResolved && !dateParameters.isComplete) {
      const prompt = missingDatesPrompt(dateParameters.requiredParameters);
      return (
        <>
          <Text variant="h3" marginBottom="md">
            {prompt.title}
          </Text>
          <Text variant="secondary-md">{prompt.description}</Text>
        </>
      );
    }
    return <LoadingIndicator />;
  })();

  return (
    <FullscreenTakeover disablePortal>
      <FullscreenTakeover.Header
        title={arenaName}
        subtitle={template.name}
        leftActionContent={
          <Button variant="muted" icon="xmark" onClick={navigateBack}>
            Exit
          </Button>
        }
      />
      <FullscreenTakeover.Body className={styles.viewerBody}>
        <Flex className={styles.viewerWrapper}>
          <PreviewPanel
            pdfUrl={pdfUrl}
            isRendering={isRendering}
            error={dateParameters.loadError ?? renderError}
            errorTitle="Preview failed"
            pdfViewerClassName={styles.pdfViewer}
            placeholder={placeholder}
          />
          <TemplateDetailsSidebar
            template={sidebarTemplate}
            isSystemTemplate={isSystemTemplate}
            duplicateArenaId={sourceArenaId}
            onArchiveClick={() => setIsArchiveModalOpen(true)}
            onUnarchiveClick={() => setIsRestoreModalOpen(true)}
            onRenameClick={() => setIsRenameModalOpen(true)}
            selectedEntityId={selectedEntity?.id ?? ""}
            selectedAccountId={selectedAccountId}
            accounts={accounts}
            accountsLoading={accountsLoading}
            onEntityChange={setSelectedEntity}
            onAccountChange={setSelectedAccountId}
            onPreviewArenaGlobalIdChange={setPreviewArenaGlobalId}
            dateParameters={dateParameters}
            isTransactionBased={transactionBased}
            selectedEntityIds={selectedEntityIds}
            onEntitiesChange={setSelectedEntityIds}
            selectedTransactionIds={selectedTransactionIds}
            onTransactionsChange={setSelectedTransactionIds}
            queryId={effectiveVersion?.queryId ?? null}
          />
        </Flex>
      </FullscreenTakeover.Body>

      <ConfirmArchiveTemplateModal
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        onConfirm={() => void handleArchive()}
        isArchiving={isArchiving}
        isSystemTemplate={isSystemTemplate}
      />

      <ConfirmRestoreTemplateModal
        isOpen={isRestoreModalOpen}
        isSystemTemplate={isSystemTemplate}
        onClose={() => setIsRestoreModalOpen(false)}
        onConfirm={() => void handleRestore()}
        isRestoring={isRestoring}
      />

      <UpdateTemplateNameModal
        isOpen={isRenameModalOpen}
        currentName={template.name}
        existingTemplateNames={existingTemplateNames}
        onClose={() => setIsRenameModalOpen(false)}
        onConfirm={(name) => void handleRename(name)}
        isUpdating={isUpdating}
      />
    </FullscreenTakeover>
  );
};
